<script lang="ts">
import { useGlobalScrollLock } from '@monogrid/vue-boost/core'
import { templateRef } from '@vueuse/core'
import { Buffer } from 'buffer'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type { ComponentPublicInstance, PropType } from 'vue'
import { computed, defineComponent, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import IconComponent from '@/components/atoms/IconComponent.vue'
import LayerBgPlane from '@/components/atoms/LayerBgPlane.vue'
import LoadingSpinner from '@/components/atoms/LoadingSpinner.vue'
import LayerLinearGrid from '@/components/molecules/LayerLinearGrid.vue'
import ProjectHeroText from '@/components/molecules/ProjectHeroText.vue'
import { useQueryVariables } from '@/functions/useQueryVariables'
import type { GetProjectQuery } from '@/graphql'
import { ContentStatus, useGetProjectQuery, useListProjectQuery } from '@/graphql'

const buildCursor = (currentProjectId: string, currentProjectDate: string) => {
  const cursor = {
    id: currentProjectId.split('/')[0],
    _contentType: 'PUBLISHED/Project',
    _customSortField0: currentProjectDate
  }

  return Buffer.from(JSON.stringify(cursor)).toString('base64')
}

export const flippingProjects = ref(false)

export default defineComponent({
  name: 'NextProject',
  components: {
    IconComponent,
    LayerBgPlane,
    LayerLinearGrid,
    LoadingSpinner,
    ProjectHeroText
  },
  props: {
    currentProject: { type: Object as PropType<GetProjectQuery['content']>, required: true }
  },
  setup (props, { attrs, slots, emit }) {
    const wrapper = ref<HTMLElement>()
    const background = ref<ComponentPublicInstance<typeof LayerLinearGrid>>()
    const header = templateRef<HTMLElement>('header')
    const useCursor = ref(true)

    const router = useRouter()
    const scrollLocked = useGlobalScrollLock()
    const listQuery = useListProjectQuery({
      // @ts-ignore
      variables: computed(() => ({
        cursor: !useCursor.value ? undefined : buildCursor(props.currentProject.id, props.currentProject.date),
        limit: 1,
        sortOn: 'date',
        status: ContentStatus.Published
      }))
    })

    const { data } = useGetProjectQuery({
      // @ts-ignore
      variables: useQueryVariables(computed(() => ({
        contentPassword: undefined,
        slug: (listQuery.data.value?.list?.items?.[0] as GetProjectQuery['content'])?._slug,
        status: ContentStatus.Published
      })))
    })
    const progress = ref(0)
    const content = computed(() => data.value?.content)

    let tl

    watch([wrapper, content, scrollLocked], ([el, _content, locked]) => {
      tl?.kill()

      if (el && _content && !locked) {
        tl = gsap.timeline({
          scrollTrigger: {
            trigger: el,
            start: 'top top',
            end: 'bottom-=10px bottom',
            scrub: true
          },
          onComplete: async () => {
            await gsap.to(header.value, { autoAlpha: 0 })
            await gsap.to(window, { scrollTo: 'max', duration: 0.5 })
            scrollLocked.value = true
            flippingProjects.value = true
            router.push({
              name: 'Project',
              params: { slug: content.value._slug }
            })
          }
        })
        tl.fromTo(progress, { value: 0 }, { value: 1, ease: 'none' })
      }

      ScrollTrigger.refresh()
    }, { flush: 'post', immediate: true })

    watch([background, progress], ([bg, _p]) => bg?.setProgress(_p))
    watch([listQuery.fetching, listQuery.data], ([fetching, data]) => {
      if (fetching || !useCursor.value) return
      useCursor.value = !!listQuery.data.value?.list?.items?.[0]
    }, { immediate: true })

    onUnmounted(() => tl?.kill())

    return {
      background,
      content,
      wrapper,
      progress
    }
  }
})
</script>

<template>
  <section ref="wrapper" class="relative">
    <div v-reveal.repeat="'scaleX'" v-reveal:options="{ duration: 1, delay: 0.3 }" class="border-horizontal origin-left bg-grey-dark top-0 z-1" />
    <div class="h-lview sticky top-0">
      <LayerBgPlane class="absolute inset-0 c-blue" />
      <LayerLinearGrid ref="background" class="absolute inset-0 c-blue" />
      <Transition>
        <LoadingSpinner v-if="!content" class="absolute-center w-7 h-7" />
        <ProjectHeroText v-else class="relative" :content="content" :revealable="false">
          <template #header>
            <div ref="header" v-reveal.group class="flex items-start justify-between">
              <span v-reveal.child class="h4">{{ $t('Next Project') }}</span>
              <div>
                <div class="overflow-hidden">
                  <div v-reveal.child="'slideUp'" class="whitespace-nowrap lg:text-right ">
                    <span>{{ $t('Keep scrolling') }}</span>
                    <IconComponent class="ml-1 w-3 h-3" icon="segment" />
                  </div>
                </div>
                <div class="progress-bar mt-1 lg:mt-1.5 h-2 w-full">
                  <div class="bg-current c-green w-full h-full origin-top-left" :style="{'transform': `scaleX(${progress})`}" />
                </div>
              </div>
            </div>
          </template>
        </ProjectHeroText>
      </Transition>
    </div>
  </section>
</template>

<style lang="scss" scoped>
section {
  @include svh(200, 'height');
}

.progress-bar {
  border: solid 1px color('grey-dark');
}
</style>
